import { createContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getMillisecondsForPastHours } from '../utils/helper-functions';

export const DjContext = createContext();

export function DjContextWrapper({ children }) {
  const [songRequests, setSongRequests] = useState([]);
  const [timeRange, setTimeRange] = useState(4);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [songFetchLoading, setSongFetchLoading] = useState(false);
  const [clientIdFilter, setClientIdFilter] = useState(null);
  const [songNameFilter, setSongNameFilter] = useState(null);
  const [refreshIntervalId, setRefreshIntervalId] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(60_000);

  useEffect(() => {
    handleGetRequests();
  }, [timeRange, refreshInterval]);

  function handleRefreshRequests() {
    handleGetRequests();
  }

  function handleGetRequests() {
    setSongFetchLoading(true);
    const reqBody = {
      getStartTime: getMillisecondsForPastHours(timeRange),
    };

    // I am using a post method here instead of get because I want more options for filtering results
    axios
      .post('/api/requests', reqBody)
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setSongRequests(data);
        }
      })
      .catch((error) => {
        console.error('! Error in handleGetRequests: ', error);
      })
      .finally(() => setSongFetchLoading(false));
  }

  const filteredSongRequests = useMemo(() => {
    // If no filters are applied, return all song requests
    if (!clientIdFilter && !songNameFilter) {
      return songRequests;
    }

    return songRequests.filter((request) => {
      // Apply filtering logic
      const matchesClientId = clientIdFilter
        ? request.requester_client_id === clientIdFilter
        : true;

      const matchesSongName = songNameFilter
        ? songNameFilter
            .toLowerCase()
            .includes(request.request_details.name.toLowerCase())
        : true;

      // Return only requests that match both clientId and songName filters
      return matchesClientId && matchesSongName;
    });
  }, [songRequests, clientIdFilter, songNameFilter]);

  function startRefreshLoop() {
    if (typeof refreshInterval !== 'number' || refreshInterval <= 1000) {
      return;
    }
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
    }
    const intervalId = setInterval(() => {
      handleGetRequests();
    }, refreshInterval);

    setRefreshIntervalId(intervalId);
  }

  function stopRefreshLoop() {
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
      setRefreshIntervalId(null);
    }
  }

  return (
    <DjContext.Provider
      value={{
        filteredSongRequests,
        timeRange,
        setTimeRange,
        songFetchLoading,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        handleRefreshRequests,
        clientIdFilter,
        setClientIdFilter,
        songNameFilter,
        setSongNameFilter,
        startRefreshLoop,
        stopRefreshLoop,
        setRefreshInterval,
        refreshInterval
      }}
    >
      {children}
    </DjContext.Provider>
  );
}
